import { useState, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import type {
  ComponentContentSchema,
  SlotGroupDefinition,
} from '@vibesites/runtime';
import {
  EditableToken,
  EditableSlotGroup,
  EditableCollectionBinding,
  useContentToken,
  useSlotGroup,
  useCollectionBinding,
  useEditMode,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';

// sessionStorage flag so the popup shows only once per live visit (cleared when
// the browser tab/session ends). In the editor we skip this so the popup stays
// available on every refresh.
const SEEN_KEY = 'rr-living-special-popup-shown';

// Survives remounts within this editor session so dismissing the popup stays
// dismissed while you work on other sections. A full page refresh resets it.
let editorDismissed = false;

interface SpecialOffer {
  title?: string | null;
  description?: string | null;
  disclaimer?: string | null;
  start_date?: string | null;
  end_date?: string | null;
  enabled?: string | null;
  [key: string]: unknown;
}

const ctaDef: SlotGroupDefinition = {
  label: 'Call Button',
  fields: {
    label: {
      label: 'Button Text',
      type: 'text',
      defaultValue: 'CALL NOW',
    },
    phone: {
      label: 'Phone Number',
      type: 'phone',
      defaultField: 'property_call_tracking_phone',
      defaultValue: '',
      suggestedFields: ['property_phone', 'property_call_tracking_phone'],
    },
  },
};

function toTelHref(raw: string): string {
  const trimmed = raw.trim();
  if (!trimmed) return '';
  if (/^tel:/i.test(trimmed)) return trimmed;
  const digits = trimmed.replace(/[^\d+]/g, '');
  return digits ? `tel:${digits}` : '';
}

function hasValue(v: unknown): v is string {
  if (v == null) return false;
  const s = String(v).trim().toLowerCase();
  return s !== '' && s !== '-' && s !== 'null' && s !== 'undefined' && s !== 'false';
}

/** Same active-window rules as SpecialOfferBanner — MH dates drive visibility. */
function isOfferActive(offer: SpecialOffer | null | undefined): boolean {
  if (!offer) return false;

  // Do not consult `offer.enabled` — in MH data it is a static always-false
  // default, so honoring it would hide every offer.

  if (!hasValue(offer.title) && !hasValue(offer.description)) return false;

  const now = Date.now();

  if (hasValue(offer.start_date)) {
    const start = new Date(offer.start_date!).getTime();
    if (!isNaN(start) && start > now) return false;
  }

  if (hasValue(offer.end_date)) {
    const end = new Date(offer.end_date!).getTime();
    if (!isNaN(end) && end < now) return false;
  }

  return true;
}

function stripHtml(html: string): string {
  if (typeof document !== 'undefined') {
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return (tmp.textContent || tmp.innerText || '').trim();
  }
  return html.replace(/<[^>]*>/g, '').trim();
}

export function SpecialPopup() {
  const offers = useCollectionBinding<SpecialOffer>('specialpopup.items', {
    collection: 'special_offers',
  });
  const image = useContentToken('specialpopup.image');
  const cta = useSlotGroup('specialpopup.cta', ctaDef);
  const editMode = useEditMode();
  const isEditMode = editMode?.isEditMode ?? false;

  const activeOffers = useMemo(
    () => (offers || []).filter(isOfferActive),
    [offers],
  );
  const hasOffers = activeOffers.length > 0;
  const offer = activeOffers[0];

  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!hasOffers) {
      setOpen(false);
      return;
    }
    if (isEditMode) {
      if (editorDismissed) return;
      const raf = requestAnimationFrame(() => setOpen(true));
      return () => cancelAnimationFrame(raf);
    }
    let seen = false;
    try {
      seen = window.sessionStorage.getItem(SEEN_KEY) === '1';
    } catch {
      // sessionStorage unavailable — show rather than suppress.
    }
    if (seen) return;
    const raf = requestAnimationFrame(() => setOpen(true));
    return () => cancelAnimationFrame(raf);
  }, [isEditMode, hasOffers]);

  const close = useCallback(
    (e?: { stopPropagation?: () => void; preventDefault?: () => void }) => {
      e?.stopPropagation?.();
      e?.preventDefault?.();
      setOpen(false);
      if (isEditMode) {
        editorDismissed = true;
        return;
      }
      try {
        window.sessionStorage.setItem(SEEN_KEY, '1');
      } catch {
        // Ignore storage failures — the popup simply won't be remembered.
      }
    },
    [isEditMode],
  );

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, close]);

  if (!hasOffers || !offer || !open) return null;

  const title = hasValue(offer.title) ? String(offer.title) : '';
  const description = hasValue(offer.description)
    ? stripHtml(String(offer.description))
    : '';
  const disclaimer = hasValue(offer.disclaimer)
    ? stripHtml(String(offer.disclaimer))
    : '';
  const ctaLabel = (cta.label || '').trim();
  const ctaHref = toTelHref(String(cta.phone || ''));
  const showCta = hasValue(ctaLabel);

  return createPortal(
    <div
      className="spop-overlay"
      role="dialog"
      aria-modal="true"
      aria-label={title || 'Special offer'}
      onClick={close}
    >
      <div
        className="spop-modal"
        onClick={(e) => e.stopPropagation()}
        onPointerDown={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          className="spop-close"
          aria-label="Close"
          onClick={close}
          onPointerDown={(e) => {
            e.stopPropagation();
            close(e);
          }}
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>

        <div className="spop-card">
          <div className="spop-photo">
            <EditableToken
              path="specialpopup.image"
              type="image"
              as="div"
              label="Background Image"
              className="spop-photo-token"
            >
              <VibesitesImage
                url={image || ''}
                alt=""
                priority
                sizes="(max-width: 640px) 100vw, 480px"
                className="spop-photo-img"
              />
            </EditableToken>
          </div>

          <div className="spop-panel">
            <EditableCollectionBinding
              bindingPath="specialpopup.items"
              label="Special Offers"
              className="spop-offer"
            >
              {title ? (
                <h2 className="spop-headline font-heading">{title}</h2>
              ) : null}
              {description ? (
                <p className="spop-body font-body">{description}</p>
              ) : null}
              {disclaimer ? (
                <p className="spop-disclaimer font-body">{disclaimer}</p>
              ) : null}
            </EditableCollectionBinding>

            {showCta ? (
              <EditableSlotGroup
                groupPath="specialpopup.cta"
                definition={ctaDef}
                className="spop-cta-wrap"
              >
                {ctaHref ? (
                  <a href={ctaHref} className="spop-cta">
                    {ctaLabel}
                  </a>
                ) : (
                  <span className="spop-cta">{ctaLabel}</span>
                )}
              </EditableSlotGroup>
            ) : null}
          </div>
        </div>
      </div>

      <style>{`
        .spop-overlay {
          position: fixed;
          inset: 0;
          z-index: 2000;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 1.25rem;
          background: rgba(0, 0, 0, 0);
          animation: spop-overlay-in 0.3s ease forwards;
        }
        @keyframes spop-overlay-in {
          to { background: rgba(0, 0, 0, 0.62); }
        }

        .spop-modal {
          position: relative;
          width: min(100%, 480px);
          opacity: 0;
          transform: translateY(16px) scale(0.97);
          animation: spop-modal-in 0.35s cubic-bezier(0.16, 1, 0.3, 1) 0.05s forwards;
        }
        @keyframes spop-modal-in {
          to { opacity: 1; transform: translateY(0) scale(1); }
        }

        .spop-card {
          position: relative;
          display: flex;
          flex-direction: column;
          width: 100%;
          overflow: hidden;
          background: var(--vibe-color-background, #fff);
          box-shadow: 0 24px 64px rgba(0, 0, 0, 0.4);
        }

        .spop-photo {
          position: relative;
          width: 100%;
          aspect-ratio: 3 / 2;
          overflow: hidden;
        }
        .spop-photo-token {
          display: block;
          width: 100%;
          height: 100%;
        }
        .spop-photo-img {
          display: block;
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .spop-panel {
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
          gap: clamp(0.75rem, 2.5vw, 1.15rem);
          padding: clamp(1.5rem, 5vw, 2.25rem) clamp(1.25rem, 5vw, 2.25rem) clamp(1.75rem, 5vw, 2.5rem);
          background: var(--vibe-color-accent, #FFF1DA);
          color: var(--vibe-color-text-on-secondary, #000);
        }

        .spop-offer {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: clamp(0.75rem, 2.5vw, 1.15rem);
          width: 100%;
        }

        .spop-headline {
          margin: 0;
          color: inherit;
          font-size: clamp(1.5rem, 5vw, 2rem);
          font-weight: 700;
          line-height: 1.1;
          letter-spacing: 0.01em;
          text-transform: uppercase;
        }

        .spop-body {
          margin: 0;
          color: inherit;
          font-size: clamp(1rem, 2.8vw, 1.15rem);
          font-weight: 400;
          line-height: 1.5;
          max-width: 36ch;
        }

        .spop-disclaimer {
          margin: 0;
          color: inherit;
          font-size: 0.9rem;
          font-weight: 500;
          line-height: 1.55;
          max-width: 40ch;
        }

        .spop-cta-wrap {
          display: block;
          margin-top: 0.25rem;
        }
        .spop-cta {
          display: inline-block;
          padding: 0.85rem 1.9rem;
          border: 2px solid var(--vibe-color-primary, #000);
          border-radius: 999px;
          background: var(--vibe-color-primary, #000);
          color: var(--vibe-color-text-on-primary, #fff);
          font-family: var(--vibe-font-body, inherit);
          font-size: 0.85rem;
          font-weight: 700;
          letter-spacing: 0.06em;
          text-transform: uppercase;
          text-decoration: none;
          cursor: pointer;
          transition: background 0.2s, color 0.2s;
        }
        a.spop-cta:hover {
          background: transparent;
          color: var(--vibe-color-primary, #000);
        }

        .spop-close {
          position: absolute;
          top: 14px;
          right: 14px;
          z-index: 3;
          display: flex;
          align-items: center;
          justify-content: center;
          width: 40px;
          height: 40px;
          border-radius: 50%;
          border: none;
          background: rgba(0, 0, 0, 0.5);
          color: var(--vibe-color-text-on-primary, #fff);
          cursor: pointer;
          transition: transform 0.2s, background 0.2s;
        }
        .spop-close:hover {
          transform: scale(1.08);
          background: rgba(0, 0, 0, 0.72);
        }

        @media (max-width: 640px) {
          .spop-overlay { padding: 0.85rem; }
          .spop-modal { width: min(100%, 400px); }
          .spop-close { top: 10px; right: 10px; width: 36px; height: 36px; }
        }

        @media (prefers-reduced-motion: reduce) {
          .spop-overlay { animation: none; background: rgba(0, 0, 0, 0.62); }
          .spop-modal { animation: none; opacity: 1; transform: none; }
        }
      `}</style>
    </div>,
    document.body,
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'SpecialPopup',
  displayName: 'Special Popup',
  tokens: {
    'specialpopup.image': {
      label: 'Background Image',
      type: 'image',
      defaultField: 'property_featured_image',
      defaultValue: '',
    },
  },
  slotGroups: {
    'specialpopup.cta': ctaDef,
  },
  collectionBindings: {
    'specialpopup.items': {
      collection: 'special_offers',
      label: 'Special Offers',
    },
  },
};

(SpecialPopup as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default SpecialPopup;
